import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Mail, Globe, Lock, Code2, Building2, ShieldAlert, ShieldCheck,
  Network, Share2, FileText,
  type LucideIcon
} from 'lucide-react';
import { startScan, CURRENT_SCAN_KEY, persistScan } from '../lib/api';
import { normalizeDomain } from '../lib/domain';
import { SCAN_MODULES, type ScanEvent } from '../types';
import { Footer } from '../components/Footer';
import { ThemeToggle } from '../components/ThemeToggle';

// Cloudflare Turnstile site key. Public, baked in at build time. When set, the
// scan form shows the widget and a verified token is required; the Worker
// enforces it. When unset, the form behaves exactly as before.
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

// ---------------------------------------------------------------------------
// Live console state — a view model driven by the scan's streamed events.
// ---------------------------------------------------------------------------
type ModStatus = 'queued' | 'start' | 'ok' | 'fail';
interface ModView { id: string; label: string; status: ModStatus; ms?: number; brief?: string }

const initialMods = (): ModView[] => SCAN_MODULES.map((m) => ({ ...m, status: 'queued' }));

function useScanConsole() {
  const [mods, setMods] = useState<ModView[]>(initialMods);

  const reset = useCallback(() => setMods(initialMods()), []);

  const onEvent = useCallback((e: ScanEvent) => {
    if (e.type === 'start') {
      setMods(e.modules.map((m) => ({ ...m, status: 'queued' })));
    } else if (e.type === 'module') {
      setMods((prev) =>
        prev.map((m) =>
          m.id === e.id
            ? { ...m, label: e.label, status: e.status, ms: e.ms ?? m.ms, brief: e.brief ?? m.brief }
            : m
        )
      );
    }
  }, []);

  return { mods, reset, onEvent };
}

export function Home() {
  const [domain, setDomain] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const autoRunRef = useRef<string | null>(null);

  const scanConsole = useScanConsole();

  const resetTurnstile = () => {
    setToken(null);
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  };

  const m = useMutation({
    mutationFn: (d: string) => startScan(d, token ?? undefined, scanConsole.onEvent),
    onMutate: () => scanConsole.reset(),
    onSuccess: (res) => {
      qc.setQueryData(CURRENT_SCAN_KEY, res);
      persistScan(res); // survive refresh / back-button (tab-scoped)
      navigate('/results/executive');
    },
    // Turnstile tokens are single-use; get a fresh one for the next scan.
    onError: () => { if (TURNSTILE_SITE_KEY) resetTurnstile(); }
  });

  // Render the Turnstile widget (managed mode) once the script has loaded.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    let cancelled = false;
    const render = () => {
      if (cancelled || !widgetRef.current || !window.turnstile || widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(widgetRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        action: 'scan',
        theme: 'auto',
        callback: (t: string) => setToken(t),
        'expired-callback': () => setToken(null),
        'error-callback': () => setToken(null)
      });
    };
    if (window.turnstile) { render(); return; }
    const existing = document.getElementById('cf-turnstile-script');
    if (existing) { existing.addEventListener('load', render); return () => { cancelled = true; }; }
    const s = document.createElement('script');
    s.id = 'cf-turnstile-script';
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    s.async = true; s.defer = true;
    s.onload = render;
    document.head.appendChild(s);
    return () => { cancelled = true; };
  }, []);

  // Deep link: /?domain=example.com auto-starts a scan (used by the main site).
  // With Turnstile on, defer the run until a token arrives.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('domain');
    if (q && q.trim()) {
      setDomain(q.trim());
      if (!TURNSTILE_SITE_KEY) m.mutate(q.trim());
      else autoRunRef.current = q.trim();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire the deferred deep-link scan as soon as the token is ready.
  useEffect(() => {
    if (autoRunRef.current && token) {
      const d = autoRunRef.current;
      autoRunRef.current = null;
      m.mutate(d);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const needsToken = !!TURNSTILE_SITE_KEY;
  const canScan = !!domain.trim() && !m.isPending && (!needsToken || !!token);

  // Preview what the scanner will actually scan, so pasting a URL or a www host
  // (which would otherwise tank email/DNS results) is visible before running.
  const raw = domain.trim().toLowerCase();
  const target = normalizeDomain(raw);
  const cleaned = !!target && target !== raw;
  const hostish = raw.replace(/^https?:\/\//, '').split('/')[0];
  const wwwStripped = !!target && hostish.startsWith('www.') && !target.startsWith('www.');

  const phase: ConsolePhase = m.isPending ? 'running' : m.isError ? 'error' : 'idle';

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden">
      <header className="relative mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-6">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="PROTBYTE Sentinel" width={34} height={34} draggable={false} />
          <span className="font-display text-xl font-bold tracking-tight text-ink">PROTBYTE <span className="text-accent">Sentinel</span></span>
        </div>
        <ThemeToggle />
      </header>

      <main className="relative mx-auto w-full max-w-6xl flex-1 px-6 pb-24 pt-8 sm:pt-12">
        {/* HERO — asymmetric split: the pitch on the left, the machine on the
            right. The console is the signature element; it previews the target
            while idle and streams real analyzer output during a scan. */}
        <div className="grid items-start gap-10 lg:grid-cols-[1.02fr_0.98fr] lg:gap-12">
          <div className="text-center lg:text-left">
            <span className="eyebrow-pill mb-5">
              <span className="ok-dot" aria-hidden="true" />
              External security &amp; compliance assessment
            </span>
            <h1 className="mt-1 text-balance font-display text-4xl font-semibold tracking-[-0.03em] text-ink sm:text-5xl xl:text-6xl">
              Your security posture,{' '}
              <span className="text-accent">seen end to end.</span>
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-pretty text-lg text-ink-2 lg:mx-0">
              100+ external checks across email, DNS, web and TLS, hosting and ASN,
              subdomains and takeover risk, exposed assets, your tech and vendor
              stack, and known CVEs. Read back to you as an executive report mapped
              to{' '}
              <strong className="font-semibold text-ink">
                CMMC Level 2, NIST 800-171, SOC 2, ISO 27001, NIST CSF, and PCI DSS v4
              </strong>.
            </p>

            <form
              className="mx-auto mt-8 flex max-w-xl flex-col gap-3 sm:flex-row lg:mx-0"
              onSubmit={(e) => { e.preventDefault(); if (canScan) m.mutate(domain.trim()); }}
            >
              <input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="example.com"
                aria-label="Domain to scan"
                autoFocus
                inputMode="url"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                className="flex-1 rounded-xl border border-line-2 bg-panel px-4 py-3.5 text-base text-ink shadow-sm
                           focus:border-accent focus:outline-none focus:ring-4 focus:ring-accent/20"
              />
              <button type="submit" disabled={!canScan} className="btn-accent">
                {m.isPending ? 'Scanning…' : needsToken && !token ? 'Verifying…' : 'Run scan'}
              </button>
            </form>

            {/* Normalization preview — shown only when we cleaned the input. */}
            {cleaned && !m.isPending && (
              <p className="mt-3 text-sm text-ink-3">
                We&apos;ll scan <span className="font-semibold text-ink-2">{target}</span>
                {wwwStripped && (
                  <span>, your root domain, for accurate email &amp; DNS results</span>
                )}
              </p>
            )}

            {/* Turnstile widget — only rendered when a site key is configured. */}
            {TURNSTILE_SITE_KEY && (
              <div className="mt-4 flex justify-center lg:justify-start">
                <div ref={widgetRef} />
              </div>
            )}

            {m.isError && (
              <p className="mt-4 text-sm text-sev-crit">
                {(m.error as Error)?.message ?? 'Scan failed.'}
              </p>
            )}

            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-xs text-ink-3 lg:justify-start">
              <TrustChip>No signup</TrustChip>
              <TrustChip>Nothing stored</TrustChip>
              <TrustChip>Free, ~5 second scan</TrustChip>
            </div>
          </div>

          {/* The live console. */}
          <LiveConsole
            target={target || (raw ? hostish : '')}
            mods={scanConsole.mods}
            phase={phase}
          />
        </div>

        {/* Capability tiles, concrete instead of generic */}
        <div className="mx-auto mt-16 grid max-w-5xl grid-cols-1 gap-3 text-left sm:grid-cols-2 lg:grid-cols-3">
          <Tile
            Icon={Mail}
            title="Email Authentication"
            body="SPF with RFC 7208 lookup-limit checks, DKIM at exact key strength, DMARC, BIMI, MTA-STS, TLS-RPT, reporting-platform detection across 34 DMARC vendors, and authoritative Microsoft 365 tenant-cloud detection (Commercial, GCC High, DoD, 21Vianet)"
          />
          <Tile
            Icon={Globe}
            title="DNS & Registry"
            body="DNSSEC, CAA, A/AAAA/MX/NS/TXT/SOA, nameserver redundancy, and authoritative WHOIS/RDAP per TLD with domain-age risk"
          />
          <Tile
            Icon={Lock}
            title="Web & Transport"
            body="HSTS, CSP, X-Frame-Options, Referrer-Policy, security.txt (RFC 9116), plus certificate history, expiry, and issuer trust"
          />
          <Tile
            Icon={Network}
            title="Infrastructure & ASN"
            body="Network owner and ASN via Team Cymru, reverse DNS, announced BGP prefix and country, plus hosting and CDN identification"
          />
          <Tile
            Icon={Share2}
            title="Subdomains & Takeover"
            body="Subdomain discovery from Certificate Transparency, dangling-CNAME takeover detection across 20+ SaaS providers, and internal/dev host surfacing"
          />
          <Tile
            Icon={Code2}
            title="Tech Stack"
            body="4,000+ technologies fingerprinted from headers, cookies, HTML, meta tags, JS bundles, robots.txt, and manifests, with version detection and dependency inference"
          />
          <Tile
            Icon={Building2}
            title="Vendor Surface"
            body="SaaS the company actually uses, inferred from your DNS, Google Workspace, Microsoft 365, Slack, Stripe, Atlassian, Okta, OpenAI, Anthropic, HackerOne, and 80+ others"
          />
          <Tile
            Icon={ShieldAlert}
            title="Exposure & CVEs"
            body="Probes for public .git/.env, admin panels, CORS misconfig, plus real CVE matching via OSV.dev"
          />
          <Tile
            Icon={FileText}
            title="Executive Reporting"
            body="A plain-English executive summary, a prioritized fix list (effort and business impact), and copy-paste remediation for every fixable finding (DNS records, security-header config), plus an overall 0 to 10 posture score and a full styled PDF export for leadership"
            className="sm:col-span-2 lg:col-span-1"
          />

          {/* Compliance mapping, the differentiator, full width, CMMC first */}
          <div className="panel p-4 sm:col-span-2 lg:col-span-3">
            <div className="flex items-start gap-3">
              <span className="reticle grid h-9 w-9 shrink-0 place-items-center rounded-lg text-accent"
                    style={{ background: 'color-mix(in oklab, var(--t-accent) 12%, transparent)' }}>
                <ShieldCheck className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink">Compliance Mapping</p>
                <p className="mt-1 text-xs leading-relaxed text-ink-2">
                  Every finding mapped to CMMC Level 2, NIST SP 800-171, SOC 2,
                  ISO 27001, NIST CSF, and PCI DSS v4, with satisfied / partial /
                  gap status on each control.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live console — the signature element. Always ink (machine surface), in both
// themes. Idle: previews the target and lists the modules queued. Running:
// each row flips to a spinner then a verdict as the server streams events.
// ---------------------------------------------------------------------------
type ConsolePhase = 'idle' | 'running' | 'error';

const PHASE_LABEL: Record<ConsolePhase, string> = {
  idle: 'READY',
  running: 'RUNNING',
  error: 'ERROR'
};

function LiveConsole({ target, mods, phase }: { target: string; mods: ModView[]; phase: ConsolePhase }) {
  const done = mods.filter((m) => m.status === 'ok' || m.status === 'fail').length;
  const total = mods.length;

  return (
    <div className="console w-full p-0 text-left font-mono">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-sky-400/15 px-4 py-3 text-xs">
        <span className={phase === 'running' ? 'ok-dot' : 'inline-block h-[7px] w-[7px] rounded-full bg-slate-500'} aria-hidden="true" />
        <span className="text-slate-400">sentinel</span>
        <span className="text-slate-600" aria-hidden="true">/</span>
        <span className="truncate text-sky-100">{target || 'example.com'}</span>
        <span
          className={
            'ml-auto shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-[0.08em] ' +
            (phase === 'running'
              ? 'bg-sky-400/15 text-sky-300'
              : phase === 'error'
                ? 'bg-amber-400/15 text-amber-300'
                : 'bg-slate-500/15 text-slate-400')
          }
        >
          {PHASE_LABEL[phase]}
        </span>
      </div>

      {/* Module rows */}
      <ul
        className="space-y-px px-2 py-2 text-[13px]"
        role="status"
        aria-live="polite"
        aria-label="Scan progress"
      >
        {mods.map((mod) => (
          <li
            key={mod.id}
            className={
              'flex items-center gap-2.5 rounded-md px-2 py-1.5 ' +
              (mod.status === 'start' ? 'bg-sky-400/[0.06]' : '')
            }
          >
            <Glyph status={mod.status} />
            <span
              className={
                'shrink-0 ' +
                (mod.status === 'queued' ? 'text-slate-500' : 'text-sky-50/90')
              }
            >
              {mod.label}
            </span>
            <span className="min-w-0 flex-1 truncate text-slate-500">
              {mod.status === 'ok' || mod.status === 'fail' ? mod.brief : ''}
            </span>
            {(mod.status === 'ok' || mod.status === 'fail') && typeof mod.ms === 'number' && (
              <span className="tnum shrink-0 text-[11px] text-slate-600">{mod.ms}ms</span>
            )}
          </li>
        ))}
      </ul>

      {/* Footer */}
      <div className="flex items-center gap-2 border-t border-sky-400/15 px-4 py-2.5 text-[11px] text-slate-500">
        <span className="tnum">{done}/{total} modules</span>
        <span className="text-slate-700" aria-hidden="true">·</span>
        <span className="truncate">
          {phase === 'running'
            ? 'streaming analyzer output'
            : phase === 'error'
              ? 'scan interrupted'
              : 'nothing is stored, results live only in your browser'}
        </span>
        {phase === 'running' && (
          <span className="ml-auto animate-pulse text-sky-400 motion-reduce:animate-none" aria-hidden="true">▍</span>
        )}
      </div>
    </div>
  );
}

function Glyph({ status }: { status: ModStatus }) {
  if (status === 'ok') return <span className="w-3.5 shrink-0 text-center text-emerald-400" aria-hidden="true">✓</span>;
  if (status === 'fail') return <span className="w-3.5 shrink-0 text-center text-amber-400" aria-hidden="true">!</span>;
  if (status === 'start')
    return (
      <span className="inline-block w-3.5 shrink-0 animate-pulse text-center text-sky-400 motion-reduce:animate-none" aria-hidden="true">
        ▸
      </span>
    );
  return <span className="w-3.5 shrink-0 text-center text-slate-700" aria-hidden="true">·</span>;
}

function TrustChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-1.5 w-1.5 rounded-full bg-sev-ok" />
      {children}
    </span>
  );
}

function Tile({ Icon, title, body, className = '' }: { Icon: LucideIcon; title: string; body: string; className?: string }) {
  return (
    <div className={`panel panel-hover p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <span className="reticle grid h-9 w-9 shrink-0 place-items-center rounded-lg text-accent"
              style={{ background: 'color-mix(in oklab, var(--t-accent) 12%, transparent)' }}>
          <Icon className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink">{title}</p>
          <p className="mt-1 text-xs leading-relaxed text-ink-2">{body}</p>
        </div>
      </div>
    </div>
  );
}
